<template>
    <card title="Compound Preparation">
        <div>
            <!-- Mode Selection (only shown at start if not specified in route) -->
            <div v-if="!batch && !modeInternal">
                <b-row class="mb-3">
                    <b-col>
                        <h4>Select Preparation Mode</h4>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-button variant="primary" size="lg" block @click="selectMode('batch')">
                            <i class="fas fa-flask"></i> Create Batch for Inventory
                        </b-button>
                    </b-col>
                    <b-col>
                        <b-button variant="info" size="lg" block @click="selectMode('prescription')">
                            <i class="fas fa-prescription"></i> Prepare for Prescription
                        </b-button>
                    </b-col>
                </b-row>
            </div>

            <!-- Batch Initialization -->
            <div v-if="modeInternal && !batch">
                <b-row class="mb-3">
                    <b-col>
                        <h4>{{ modeTitle }}</h4>
                    </b-col>
                </b-row>
                <b-row v-if="modeInternal === 'batch'" align-v="end">
                    <b-col cols="6">
                        <SearchComponent v-model="compoundSearch" label="Select Compound" search-u-r-l="/Compound"
                            @input="onCompoundSelected" />
                    </b-col>
                    <b-col cols="3">
                        <fg-input v-model.number="batchSize" label="Batch Size" type="number" step="0.01" />
                    </b-col>
                    <b-col cols="3" class="pt-4">
                        <b-button variant="success" @click="startBatch" :disabled="!selectedCompound || !batchSize || !selectedCompound.compoundID || !selectedCompound.steps || selectedCompound.steps.length === 0">
                            Start Preparation
                        </b-button>
                    </b-col>
                </b-row>
                <b-row v-if="modeInternal === 'prescription'">
                    <b-col cols="8">
                        <SearchComponent v-model="selectedPrescription" label="Select Prescription" search-u-r-l="/Prescription"
                            @input="onPrescriptionSelected" />
                        <small v-if="selectedPrescription && selectedPrescription.rxNumber" class="text-muted">
                            <strong>Dispensed Quantity:</strong> {{ selectedPrescription.dispensed }} {{ selectedPrescription.productNameShort }}
                        </small>
                    </b-col>
                    <b-col cols="4" class="pt-4">
                        <b-button variant="success" @click="startBatchForPrescription" :disabled="!selectedPrescription || !selectedPrescription.rxNumber || !selectedCompound || !selectedCompound.compoundID || !selectedCompound.steps || selectedCompound.steps.length === 0">
                            Start Preparation
                        </b-button>
                    </b-col>
                </b-row>
            </div>

            <!-- Batch Preparation Workflow -->
            <div v-if="batch && currentStep">
                <b-row class="mb-3">
                    <b-col>
                        <h4>{{ selectedCompound?.name }}</h4>
                        <p class="text-muted">
                            Batch: {{ batch.batchNumber }} | Size: {{ batch.batchSize }} {{ selectedCompound?.unitOfMeasure }}
                            <span v-if="modeInternal === 'prescription' && batch.rxNumber">
                                | Prescription: {{ batch.storeId }}-{{ batch.rxNumber }}-{{ batch.rfNumber || 0 }}
                            </span>
                        </p>
                    </b-col>
                </b-row>

                <!-- Ingredients and Steps Row -->
                <b-row class="mb-3">
                    <!-- Ingredients Needed (Left Half) -->
                    <b-col cols="6">
                        <b-alert variant="warning" :show="selectedCompound?.ingredients?.length > 0 && !selectedCompound?.packageSize">
                            <i class="fas fa-exclamation-triangle" />
                            <strong>Package Size Not Set</strong><br />
                            This compound does not have a Package Size configured. Please edit the compound record and set the Package Size
                            to enable ingredient quantity scaling. Without this, the system cannot calculate the correct ingredient amounts for this batch.
                        </b-alert>

                        <b-card v-if="scaledIngredients.length > 0">
                            <b-card-title>
                                <i class="fas fa-flask" /> Ingredients Needed
                            </b-card-title>

                            <!-- Preset Selector -->
                            <b-row v-if="presets.length > 0" class="mb-2">
                                <b-col cols="6">
                                    <label class="mb-0"><small><strong>Ingredient Preset:</strong></small></label>
                                    <b-form-select
                                        v-model="selectedPresetId"
                                        size="sm"
                                        @change="applyPreset"
                                    >
                                        <option :value="null">Default Formula</option>
                                        <option v-for="p in presets" :key="p.id" :value="p.id">
                                            {{ p.name }}{{ p.isDefault ? ' (Default)' : '' }}
                                        </option>
                                    </b-form-select>
                                </b-col>
                            </b-row>

                            <b-alert :show="hasIngredientFromDifferentStore" variant="warning" class="mb-2">
                                <i class="fas fa-exclamation-triangle"></i>
                                <strong>Cross-Store Ingredient:</strong> One or more selected lots are from a different store than the batch destination.
                            </b-alert>

                            <b-table-simple hover small>
                                <b-thead>
                                    <b-tr>
                                        <b-th>Ingredient</b-th>
                                        <b-th class="text-right">Formula Qty</b-th>
                                        <b-th class="text-right">Qty Needed</b-th>
                                        <b-th>Lot Number</b-th>
                                        <b-th>Expiration</b-th>
                                    </b-tr>
                                </b-thead>
                                <b-tbody>
                                    <b-tr v-for="(ingredient, index) in scaledIngredients" :key="index">
                                        <b-td>
                                            <span>{{ ingredient.name }}</span>
                                            <b-badge v-if="ingredient.isSwapped" variant="warning" class="ml-1">
                                                <i class="fas fa-exchange-alt"></i> Swapped
                                            </b-badge>
                                        </b-td>
                                        <b-td class="text-right">
                                            {{ ingredient.formulaQuantity.toFixed(2) }} {{ ingredient.unitOfMeasure }}
                                        </b-td>
                                        <b-td class="text-right">
                                            <strong>{{ ingredient.scaledQuantity.toFixed(2) }} {{ ingredient.unitOfMeasure }}</strong>
                                        </b-td>
                                        <b-td>
                                            <b-form-select
                                                v-if="getIngredientLots(ingredient).length > 0"
                                                :value="selectedLotIds[ingredient.compoundIngredientID] || null"
                                                size="sm"
                                                :options="buildIngredientLotOptions(ingredient)"
                                                class="mb-1"
                                                @change="onLotSelectedForIngredient(ingredient, $event)"
                                            />
                                            <input
                                                v-if="!selectedLotIds[ingredient.compoundIngredientID]"
                                                type="text"
                                                class="form-control form-control-sm"
                                                v-model="ingredient.lotNumber"
                                                placeholder="Lot #" />
                                        </b-td>
                                        <b-td>
                                            <input
                                                v-if="!selectedLotIds[ingredient.compoundIngredientID]"
                                                type="date"
                                                class="form-control form-control-sm"
                                                v-model="ingredient.expirationDate" />
                                        </b-td>
                                    </b-tr>
                                </b-tbody>
                            </b-table-simple>
                            <small class="text-muted">
                                <i class="fas fa-info-circle" />
                                Quantities scaled from package size ({{ selectedCompound?.packageSize }} {{ selectedCompound?.unitOfMeasure }})
                                to batch size ({{ batch.batchSize }} {{ selectedCompound?.unitOfMeasure }})
                            </small>
                        </b-card>
                    </b-col>

                    <!-- Step List (Right Half) -->
                    <b-col cols="6">
                        <b-card>
                            <b-card-title>
                                <i class="fas fa-list-ol" /> Preparation Steps
                            </b-card-title>
                            <b-list-group>
                                <b-list-group-item
                                    v-for="(step, index) in batch.steps"
                                    :key="step.id"
                                    :variant="index < currentStepIndex ? 'success' : index === currentStepIndex ? 'primary' : ''"
                                    class="d-flex align-items-center">
                                    <div class="mr-3">
                                        <i v-if="index < currentStepIndex" class="fas fa-check-circle text-success" />
                                        <i v-else-if="index === currentStepIndex" class="fas fa-arrow-circle-right text-primary" />
                                        <i v-else class="far fa-circle text-muted" />
                                    </div>
                                    <div class="flex-grow-1">
                                        <strong>Step {{ step.stepNumber }}:</strong> {{ step.description }}
                                        <div v-if="index < currentStepIndex" class="small text-muted">
                                            <i class="fas fa-check" /> Completed
                                        </div>
                                        <div v-else-if="index === currentStepIndex" class="small text-primary">
                                            <i class="fas fa-spinner fa-pulse" /> In Progress
                                        </div>
                                    </div>
                                </b-list-group-item>
                            </b-list-group>
                        </b-card>
                    </b-col>
                </b-row>

                <!-- Current Step Card -->
                <b-card>
                    <b-card-title>
                        <b-badge variant="primary">Step {{ currentStep.stepNumber }}</b-badge>
                        {{ currentStep.description }}
                    </b-card-title>

                    <!-- Weight Entry (if required) -->
                    <div v-if="currentStepConfig && currentStepConfig.requireWeight" class="mt-3">
                        <b-row>
                            <b-col cols="4">
                                <fg-input v-model.number="currentStep.actualWeight" label="Actual Weight"
                                    type="number" step="0.01" @input="validateWeight" />
                            </b-col>
                            <b-col cols="8">
                                <div class="mt-4">
                                    <strong>Expected:</strong> {{ scaledExpectedWeight.toFixed(2) }} {{ selectedCompound?.unitOfMeasure }}
                                    <br />
                                    <strong>Acceptable Range:</strong>
                                    {{ weightRange.min.toFixed(2) }} - {{ weightRange.max.toFixed(2) }} {{ selectedCompound?.unitOfMeasure }}
                                    <br />
                                    <b-badge v-if="currentStep.actualWeight"
                                        :variant="currentStep.withinTolerance ? 'success' : 'danger'">
                                        {{ currentStep.withinTolerance ? '✓ Within Tolerance' : '✗ Out of Tolerance' }}
                                    </b-badge>
                                </div>
                            </b-col>
                        </b-row>
                    </div>

                    <!-- Photo Capture (if required) -->
                    <div v-if="currentStepConfig && currentStepConfig.requirePicture" class="mt-3">
                        <b-row>
                            <b-col>
                                <h5>Photo Documentation</h5>
                                <div v-if="!currentStepPhotoPreview">
                                    <input type="file" accept="image/*" @change="onPhotoSelected" ref="photoInput" class="mb-2" />
                                </div>
                                <div v-else>
                                    <img :src="currentStepPhotoPreview" alt="Step Photo" class="img-thumbnail mb-2" style="max-width: 400px;" />
                                    <br />
                                    <b-button size="sm" variant="outline-danger" @click="clearPhoto">
                                        <i class="fas fa-trash" /> Remove Photo
                                    </b-button>
                                </div>
                            </b-col>
                        </b-row>
                    </div>

                    <!-- Navigation Buttons -->
                    <b-row class="mt-4">
                        <b-col>
                            <b-button variant="outline-primary" @click="previousStep" :disabled="currentStepIndex === 0">
                                <i class="fas fa-arrow-left" /> Previous
                            </b-button>
                            <b-button v-if="currentStepIndex < (batch.steps?.length || 0) - 1"
                                variant="primary" @click="nextStep" :disabled="!canProceed">
                                Next <i class="fas fa-arrow-right" />
                            </b-button>
                            <b-button v-else variant="success" @click="completeBatch" :disabled="!canProceed">
                                <i class="fas fa-check" /> Complete Batch
                            </b-button>
                        </b-col>
                    </b-row>
                </b-card>
            </div>

            <!-- Batch Completion Form -->
            <b-modal v-model="showCompletionModal" title="Complete Batch" size="lg" hide-footer>
                <b-alert :show="!hasSupportedClientService" variant="danger">
                    <div class="d-flex" style="gap: 6px;">
                        <i class="fa fa-warning"></i>
                        <strong>
                            Supported version of Scriptly Client Service not found.
                        </strong>
                    </div>
                </b-alert>

                <b-row>
                    <b-col cols="6">
                        <fg-input v-model="lotNumber" label="Lot Number" type="text" />
                    </b-col>
                    <b-col cols="6">
                        <fg-input v-model="beyondUseDate" label="Beyond Use Date" type="date" />
                    </b-col>
                </b-row>
                <b-row v-if="modeInternal === 'batch'" class="mt-2">
                    <b-col cols="4">
                        <SearchComponent v-model="selectedStore" label="Store" search-u-r-l="/Store" prepopulate required />
                    </b-col>
                    <b-col cols="4">
                        <SearchComponent v-model="selectedProgram" label="Program (Optional)" search-u-r-l="/Program" clearable prepopulate/>
                    </b-col>
                    <b-col cols="4">
                        <SearchComponent v-model="selectedLocation" label="Storage Location (Optional)" search-u-r-l="/inventory/inventory-locations" clearable/>
                    </b-col>
                </b-row>
                <b-row class="mt-2">
                    <b-col cols="6">
                        <fg-input v-model.number="packageSize" label="Package Size" type="number" step="0.01" required>
                            <template #label>
                                Package Size <span class="text-danger">*</span>
                                <small v-if="selectedCompound?.packageSize" class="text-muted">
                                    (Compound default: {{ selectedCompound.packageSize }} {{ selectedCompound.unitOfMeasure }})
                                </small>
                            </template>
                        </fg-input>
                    </b-col>
                    <b-col cols="6" class="mt-4">
                        <strong>Labels to Print:</strong> {{ labelCount }}
                        <template v-if="modeInternal === 'prescription' && labelCount > 1">
                            <br><small class="text-muted">(Labels will be numbered: "1 of {{ labelCount }}", "2 of {{ labelCount }}", etc.)</small>
                        </template>
                    </b-col>
                </b-row>
                <b-row v-if="!isLoadingPrinters" class="mt-2">
                    <b-col cols="12">
                        <label>Printer:</label>
                        <b-dropdown :text="prettyPrinterName(activeLocalPrinter)" variant="outline-primary" block>
                            <b-dropdown-item-button v-for="p in localPrinters" :key="p.name"
                                :active="p.name === activeLocalPrinter?.name" @click="activeLocalPrinter = p">
                                <i class="fa fa-print"></i>
                                {{ prettyPrinterName(p) }}
                                <div class="d-flex" style="gap: 6px;">
                                    <small v-if="p.default">Local Default</small>
                                    <small v-if="p.status !== 'Unknown'">{{ p.status }}</small>
                                </div>
                            </b-dropdown-item-button>
                            <b-dropdown-item disabled v-if="!hasLocalPrinters">
                                <i class="fa fa-info-circle" />
                                To print to local printers, install<br>the Scriptly Client Service
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-col>
                </b-row>
                <b-row class="mt-3">
                    <b-col>
                        <b-button variant="success" @click="finalizeBatch" :disabled="!canFinalize">
                            Finalize Batch
                        </b-button>
                        <b-button variant="secondary" @click="showCompletionModal = false" class="ml-2">
                            Cancel
                        </b-button>
                    </b-col>
                </b-row>
            </b-modal>
        </div>
    </card>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import SearchComponent from '@/components/SearchComponent.vue';
import { CompoundDrug, CompoundBatch, CompoundBatchSteps, CompoundSteps, CompoundBatchIngredients, CompoundPreset } from '@/models/Drug/CompoundDrug';
import { DrugProduct, DrugSource } from '@/models/Drug/DrugProduct';
import Axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { Store } from "@/models/Store";
import { Program } from "@/models/Program";
import { Prescription } from "@/models/Prescription";
import { compareVersions } from "compare-versions";
import { InventoryLocation } from "@/models/InventoryLocation";

const minClientVersion = "1.3.0";

@Component({
    name: "CompoundFillingPage",
    components: {
        SearchComponent
    }
})
export default class CompoundFillingPage extends Vue {
    @Prop() private compoundId?: string;
    @Prop() private mode?: 'batch' | 'prescription';

    private modeInternal: 'batch' | 'prescription' | null = null;
    private compoundSearch: CompoundDrug = new CompoundDrug();
    private selectedCompound: CompoundDrug | null = null;
    private selectedPrescription: Prescription = new Prescription();
    private batchSize: number | null = null;
    private batch: CompoundBatch | null = null;
    private currentStepIndex = 0;
    protected showCompletionModal = false;
    private lotNumber = '';
    private beyondUseDate = '';
    private packageSize: number | null = null;
    private currentStepPhotoFile: File | null = null;
    protected currentStepPhotoPreview: string | null = null;
    private selectedStore: Store = new Store();
    private selectedProgram: Program | null = new Program();
    private selectedLocation: InventoryLocation | null = new InventoryLocation();
    private isResumingBatch = false;

    // Printer management properties
    protected isLoadingPrinters = false;
    protected localPrinters: any[] = [];
    private activeLocalPrinter: any | null = null;
    protected hasSupportedClientService: boolean | null = null;
    protected labelZpl: any = {};

    // Preset and ingredient swap state
    private presets: CompoundPreset[] = [];
    private selectedPresetId: number | null = null;
    private ingredientSwaps: Record<number, { drugID: number; drugSource: number; drugName: string; quantity?: number; unitOfMeasure?: string }> = {};
    private ingredientAvailableLots: Record<number, Array<{ inventoryId: number; lotNumber: string; expirationDate: Date | null; quantity: number; storeId: number; storeName: string }>> = {};
    private selectedLotIds: Record<number, number | null> = {};

    async mounted() {
        // Check if resuming an existing batch
        const batchId = this.$route.query.batchId as string;
        if (batchId) {
            await this.resumeExistingBatch(parseInt(batchId));
        } else {
            // Initialize from route params if provided
            if (this.mode) {
                this.modeInternal = this.mode;
            }
            if (this.compoundId) {
                await this.loadCompoundFromRoute(parseInt(this.compoundId));
            }
        }

        // Initialize printer support
        this.getLocalPrinters();
        this.getSupportedClientService();
    }

    get modeTitle() {
        return this.modeInternal === 'batch' ? 'Create Batch for Inventory' : 'Prepare for Prescription';
    }

    async loadCompoundFromRoute(compoundId: number) {
        try {
            await this.loadCompoundDetails(compoundId);
            // loadCompoundDetails already sets selectedCompound with steps loaded
            this.compoundSearch = this.selectedCompound!;
        } catch (error) {
            console.error('Error loading compound from route', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async resumeExistingBatch(batchId: number) {
        try {
            this.isResumingBatch = true;

            // Load the batch
            const batchResponse = await Axios.get<CompoundBatch>(`/CompoundBatch/${batchId}`);
            this.batch = batchResponse.data;

            console.log('Resuming batch:', {
                batchID: this.batch.id,
                batchNumber: this.batch.batchNumber,
                batchSize: this.batch.batchSize,
                compoundID: this.batch.compoundID,
                status: this.batch.status,
                rxNumber: this.batch.rxNumber
            });

            // Determine mode based on whether it's a prescription batch
            if (this.batch.rxNumber) {
                this.modeInternal = 'prescription';

                // Load prescription details
                const rxID = `${this.batch.storeId}-${this.batch.rxNumber}-${this.batch.rfNumber || 0}`;
                const prescriptionResponse = await Axios.get<Prescription>(`/api/Prescription/${rxID}`);
                this.selectedPrescription = new Prescription(prescriptionResponse.data);
            } else {
                this.modeInternal = 'batch';
            }

            // Load compound details
            await this.loadCompoundDetails(this.batch.compoundID);
            this.compoundSearch = this.selectedCompound!;
            this.batchSize = this.batch.batchSize;

            // Find the current step (first incomplete step)
            if (this.batch.steps && this.batch.steps.length > 0) {
                const incompleteStepIndex = this.batch.steps.findIndex(step => !step.completed);
                this.currentStepIndex = incompleteStepIndex >= 0 ? incompleteStepIndex : 0;
            }

            this.$notification(NotificationOptions.success(`Resuming batch ${this.batch.batchNumber}`));
            this.loadAvailableLotsForIngredients();
        } catch (error) {
            console.error('Error resuming batch', error);
            this.$notification(NotificationOptions.error('Failed to resume batch. Please try again.'));
            // Redirect back to batch audit page
            this.$router.push({ name: 'CompoundBatchAudit' });
        } finally {
            this.isResumingBatch = false;
        }
    }

    get currentStep(): CompoundBatchSteps | null {
        if (!this.batch?.steps || this.currentStepIndex >= this.batch.steps.length) return null;
        return this.batch.steps[this.currentStepIndex];
    }

    get currentStepConfig(): CompoundSteps | null {
        if (!this.selectedCompound?.steps || !this.currentStep) return null;
        return this.selectedCompound.steps.find(s => s.stepNumber === this.currentStep!.stepNumber) || null;
    }

    get scaledExpectedWeight(): number {
        const expectedWeight = this.currentStepConfig?.expectedWeight;
        if (expectedWeight == null) return 0;

        // Scale expected weight based on batch size (same as ingredients)
        const scaleFactor = this.batch?.batchSize && this.selectedCompound?.packageSize
            ? this.batch.batchSize / this.selectedCompound.packageSize
            : 1;
        return expectedWeight * scaleFactor;
    }

    get weightRange(): { min: number, max: number } {
        const weightTolerance = this.currentStepConfig?.weightTolerance;

        // Explicitly check for null/undefined, not falsy (0 could be valid)
        if (this.scaledExpectedWeight == null || weightTolerance == null) {
            console.warn('Missing weight data:', {
                scaledExpectedWeight: this.scaledExpectedWeight,
                weightTolerance,
                currentStep: this.currentStep,
                currentStepConfig: this.currentStepConfig,
                selectedCompoundSteps: this.selectedCompound?.steps
            });
            return { min: 0, max: 0 };
        }

        const tolerance = weightTolerance / 100;
        return {
            min: this.scaledExpectedWeight * (1 - tolerance),
            max: this.scaledExpectedWeight * (1 + tolerance)
        };
    }

    get canProceed(): boolean {
        if (!this.currentStep || !this.currentStepConfig) return false;

        // Check weight requirement
        if (this.currentStepConfig.requireWeight) {
            if (!this.currentStep.actualWeight) return false;
            if (!this.currentStep.withinTolerance) return false;
        }

        // Check picture requirement
        if (this.currentStepConfig.requirePicture) {
            if (!this.currentStepPhotoFile && !this.currentStep.photoId) return false;
        }

        return true;
    }

    get labelCount(): number {
        if (!this.batch?.batchSize) return 0;

        // For batch mode (inventory), use the package size entered in the modal
        if (this.modeInternal === 'batch') {
            if (!this.packageSize) return 0;
            return Math.ceil(this.batch.batchSize / this.packageSize);
        }

        // For prescription mode, use the compound's package size
        if (this.modeInternal === 'prescription') {
            const compoundPackageSize = this.selectedCompound?.packageSize;
            if (!compoundPackageSize) return 1; // Default to 1 label if package size not set
            return Math.ceil(this.batch.batchSize / compoundPackageSize);
        }

        return 0;
    }

    get canFinalize(): boolean {
        if (this.modeInternal === 'batch') {
            return !!(this.lotNumber && this.beyondUseDate && this.packageSize && this.selectedStore);
        }
        if (this.modeInternal === 'prescription') {
            return !!(this.lotNumber && this.beyondUseDate);
        }
        return false;
    }

    get scaledIngredients(): Array<{
        compoundIngredientID: number;
        drugID: number;
        drugSource: number;
        name: string;
        isSwapped: boolean;
        formulaQuantity: number;
        scaledQuantity: number;
        unitOfMeasure: string;
        lotNumber: string | null;
        expirationDate: Date | null;
    }> {
        if (!this.selectedCompound?.ingredients || !this.batch?.batchSize || !this.selectedCompound?.packageSize) {
            console.log('Ingredients not available:', {
                hasIngredients: !!this.selectedCompound?.ingredients,
                ingredientsCount: this.selectedCompound?.ingredients?.length,
                hasBatchSize: !!this.batch?.batchSize,
                batchSize: this.batch?.batchSize,
                hasPackageSize: !!this.selectedCompound?.packageSize,
                packageSize: this.selectedCompound?.packageSize
            });
            return [];
        }

        const scaleFactor = this.batch.batchSize / this.selectedCompound.packageSize;

        const scaled = this.selectedCompound.ingredients.map(ingredient => {
            const swap = this.ingredientSwaps[ingredient.id];
            const effectiveDrugID = swap?.drugID ?? ingredient.drugID;
            const effectiveDrugSource = swap?.drugSource ?? ingredient.drugSource;
            const effectiveName = swap?.drugName ?? ingredient.drugName ?? 'Unknown';

            // Check if batch has ingredient data (when resuming)
            const batchIngredient = this.batch?.ingredients?.find(
                bi => bi.drugID === effectiveDrugID && bi.drugSource === effectiveDrugSource
            );

            return {
                compoundIngredientID: ingredient.id,
                drugID: effectiveDrugID,
                drugSource: effectiveDrugSource,
                name: effectiveName,
                isSwapped: !!swap,
                formulaQuantity: swap?.quantity ?? ingredient.quantity ?? 0,
                scaledQuantity: (swap?.quantity ?? ingredient.quantity ?? 0) * scaleFactor,
                unitOfMeasure: swap?.unitOfMeasure ?? ingredient.unitOfMeasure ?? this.selectedCompound?.unitOfMeasure ?? '',
                lotNumber: batchIngredient?.lotNumber || null,
                expirationDate: batchIngredient?.expirationDate || null
            };
        });

        console.log('Scaled ingredients:', scaled);
        return scaled;
    }

    selectMode(mode: 'batch' | 'prescription') {
        this.modeInternal = mode;
    }

    onCompoundSelected(compound: CompoundDrug) {
        this.selectedCompound = compound;
        this.loadCompoundDetails(compound.compoundID!);
    }

    async loadCompoundDetails(compoundId: number) {
        try {
            const response = await Axios.get<CompoundDrug>(`/Compound/${compoundId}`);
            this.selectedCompound = response.data;

            console.log('Loaded compound:', {
                compoundID: this.selectedCompound.compoundID,
                name: this.selectedCompound.name,
                packageSize: this.selectedCompound.packageSize,
                hasIngredients: !!this.selectedCompound.ingredients,
                ingredientsCount: this.selectedCompound.ingredients?.length || 0,
                ingredients: this.selectedCompound.ingredients
            });

            // Load steps
            const stepsResponse = await Axios.get<CompoundSteps[]>(`/Compound/${compoundId}/steps`);
            if (this.selectedCompound) {
                this.selectedCompound.steps = stepsResponse.data;
            }

            // Load drug names for ingredients
            await this.loadIngredientDrugNames();

            // Load presets for ingredient swapping
            await this.loadPresetsAndEquivalents(compoundId);
        } catch (error) {
            console.error('Error loading compound details', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async loadPresetsAndEquivalents(compoundId: number) {
        this.presets = [];
        this.selectedPresetId = null;
        this.ingredientSwaps = {};

        try {
            const presetsResponse = await Axios.get<CompoundPreset[]>(`/Compound/${compoundId}/presets`);
            this.presets = presetsResponse.data;
        } catch (error) {
            console.warn('Error loading presets:', error);
        }
    }

    async applyPreset() {
        this.ingredientSwaps = {};

        if (this.selectedPresetId === null) return;

        const preset = this.presets.find(p => p.id === this.selectedPresetId);
        if (!preset?.ingredients) return;

        const nameFetches: Promise<void>[] = [];

        for (const presetIngredient of preset.ingredients) {
            const originalIngredient = this.selectedCompound?.ingredients?.find(
                i => i.id === presetIngredient.compoundIngredientID
            );
            if (!originalIngredient) continue;

            // Only add to swaps if it's different from the original
            if (presetIngredient.drugID !== originalIngredient.drugID ||
                presetIngredient.drugSource !== originalIngredient.drugSource) {
                this.$set(this.ingredientSwaps, presetIngredient.compoundIngredientID, {
                    drugID: presetIngredient.drugID,
                    drugSource: presetIngredient.drugSource,
                    drugName: presetIngredient.drugName || 'Loading...',
                    quantity: presetIngredient.quantity,
                    unitOfMeasure: presetIngredient.unitOfMeasure
                });

                // Fetch drug name if not available
                if (!presetIngredient.drugName) {
                    const ingredientId = presetIngredient.compoundIngredientID;
                    nameFetches.push(
                        Axios.get(`/api/Drug/${presetIngredient.drugID}`, {
                            params: { drugSource: presetIngredient.drugSource }
                        }).then(response => {
                            const swap = this.ingredientSwaps[ingredientId];
                            if (swap && swap.drugID === presetIngredient.drugID) {
                                this.$set(this.ingredientSwaps, ingredientId, {
                                    ...swap,
                                    drugName: response.data?.productNameShort || response.data?.name || 'Unknown'
                                });
                            }
                        }).catch(() => {
                            const swap = this.ingredientSwaps[ingredientId];
                            if (swap && swap.drugID === presetIngredient.drugID) {
                                this.$set(this.ingredientSwaps, ingredientId, { ...swap, drugName: 'Unknown' });
                            }
                        })
                    );
                }
            }
        }

        if (nameFetches.length > 0) {
            await Promise.all(nameFetches);
        }

        // Reload lots for the swapped drugs
        this.loadAvailableLotsForIngredients();
    }

    async loadAvailableLotsForIngredients() {
        if (!this.selectedCompound?.ingredients) return;

        this.ingredientAvailableLots = {};
        this.selectedLotIds = {};

        const items = this.selectedCompound.ingredients.map(ingredient => {
            const swap = this.ingredientSwaps[ingredient.id];
            return {
                DrugSource: swap?.drugSource ?? ingredient.drugSource,
                PackageId: swap?.drugID ?? ingredient.drugID
            };
        });

        try {
            const response = await Axios.post<Record<string, Array<{ inventoryId: number; lotNumber: string; expirationDate: Date | null; quantity: number; storeId: number; storeName: string }>>>('/Print/available-lots-all-stores', { items });
            const lotsByKey = response.data;

            for (const ingredient of this.selectedCompound.ingredients) {
                const swap = this.ingredientSwaps[ingredient.id];
                const drugSource = swap?.drugSource ?? ingredient.drugSource;
                const drugID = swap?.drugID ?? ingredient.drugID;
                const key = `${drugSource}_${drugID}`;
                this.$set(this.ingredientAvailableLots, ingredient.id, lotsByKey[key] || []);
                this.$set(this.selectedLotIds, ingredient.id, null);
            }
        } catch (error) {
            console.warn('Error loading available lots:', error);
        }
    }

    getIngredientLots(ingredient: any): any[] {
        return this.ingredientAvailableLots[ingredient.compoundIngredientID] || [];
    }

    buildIngredientLotOptions(ingredient: any) {
        const lots = this.getIngredientLots(ingredient);
        const options: Array<{ value: number | null; text: string }> = [
            { value: null, text: '-- Manual Entry --' }
        ];
        for (const lot of lots) {
            const exp = lot.expirationDate ? new Date(lot.expirationDate).toLocaleDateString() : 'N/A';
            options.push({
                value: lot.inventoryId,
                text: `${lot.lotNumber} (Exp: ${exp}, Qty: ${lot.quantity}) - ${lot.storeName || 'Store ' + lot.storeId}`
            });
        }
        return options;
    }

    onLotSelectedForIngredient(ingredient: any, inventoryId: number | null) {
        this.$set(this.selectedLotIds, ingredient.compoundIngredientID, inventoryId);
        if (!inventoryId) {
            ingredient.lotNumber = null;
            ingredient.expirationDate = null;
            return;
        }
        const lots = this.getIngredientLots(ingredient);
        const lot = lots.find((l: any) => l.inventoryId === inventoryId);
        if (lot) {
            ingredient.lotNumber = lot.lotNumber || '';
            ingredient.expirationDate = lot.expirationDate ? new Date(lot.expirationDate).toISOString().split('T')[0] : null;
        }
    }

    get hasIngredientFromDifferentStore(): boolean {
        const currentStoreId = this.selectedStore?.id || this.batch?.storeId;
        if (!currentStoreId) return false;

        for (const ingredientId of Object.keys(this.selectedLotIds)) {
            const lotId = this.selectedLotIds[Number(ingredientId)];
            if (!lotId) continue;
            const lots = this.ingredientAvailableLots[Number(ingredientId)] || [];
            const lot = lots.find(l => l.inventoryId === lotId);
            if (lot && lot.storeId !== currentStoreId) return true;
        }
        return false;
    }

    async loadIngredientDrugNames() {
        if (!this.selectedCompound?.ingredients) return;

        const loadPromises = this.selectedCompound.ingredients.map(async (ingredient) => {
            try {
                const response = await Axios.get<DrugProduct>(`/api/Drug/${ingredient.drugID}`, {
                    params: { drugSource: ingredient.drugSource }
                });
                ingredient.drugName = response.data.productNameShort || 'Unknown Drug';
            } catch (error) {
                console.warn(`Error loading drug name for ingredient ${ingredient.drugID}:`, error);
                ingredient.drugName = 'Error Loading Name';
            }
        });

        await Promise.all(loadPromises);
    }

    async startBatch() {
        if (!this.selectedCompound || !this.batchSize) return;

        try {
            const batchData = {
                compoundID: this.selectedCompound.compoundID,
                batchSize: this.batchSize,
                storeId: null,
                rxNumber: null,
                rfNumber: null
            };

            const response = await Axios.post<CompoundBatch>('/CompoundBatch', batchData);
            this.batch = response.data;

            console.log('Batch created:', {
                batchID: this.batch.id,
                batchNumber: this.batch.batchNumber,
                batchSize: this.batch.batchSize,
                compoundID: this.batch.compoundID,
                fullBatch: this.batch
            });

            this.currentStepIndex = 0;
            this.$notification(NotificationOptions.success('Batch started successfully'));
            this.loadAvailableLotsForIngredients();
        } catch (error) {
            console.error('Error starting batch', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async onPrescriptionSelected(prescription: Prescription) {
        if (!prescription || !prescription.rxNumber) return;

        try {
            // Load full prescription details using rxID format (storeID-rxNumber-rfNumber)
            const rxID = Prescription.createRxId(prescription.storeID, prescription.rxNumber, prescription.rfNumber);
            const response = await Axios.get<Prescription>(`/api/Prescription/${rxID}`);
            this.selectedPrescription = new Prescription(response.data);

            // Load the compound from the prescription's drug
            // DrugSource.Compound = 2
            if (this.selectedPrescription.drugSource === 2) {
                await this.loadCompoundDetails(this.selectedPrescription.packageID);
                this.batchSize = this.selectedPrescription.dispensed;
            } else {
                this.$notification(NotificationOptions.warning('Selected prescription is not for a compound drug'));
                this.selectedPrescription = new Prescription();
            }
        } catch (error) {
            console.error('Error loading prescription details', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async startBatchForPrescription() {
        if (!this.selectedCompound || !this.batchSize || !this.selectedPrescription.rxNumber) return;

        try {
            const batchData = {
                compoundID: this.selectedCompound.compoundID,
                batchSize: this.batchSize,
                storeId: this.selectedPrescription.storeID,
                rxNumber: this.selectedPrescription.rxNumber,
                rfNumber: this.selectedPrescription.rfNumber || 0
            };

            const response = await Axios.post<CompoundBatch>('/CompoundBatch', batchData);
            this.batch = response.data;

            console.log('Prescription batch created:', {
                batchID: this.batch.id,
                batchNumber: this.batch.batchNumber,
                batchSize: this.batch.batchSize,
                compoundID: this.batch.compoundID,
                rxID: `${this.selectedPrescription.storeID}-${this.selectedPrescription.rxNumber}-${this.selectedPrescription.rfNumber || 0}`,
                fullBatch: this.batch
            });

            this.currentStepIndex = 0;
            this.$notification(NotificationOptions.success('Prescription compound preparation started'));
            this.loadAvailableLotsForIngredients();
        } catch (error) {
            console.error('Error starting prescription batch', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    validateWeight() {
        if (!this.currentStep?.actualWeight ||
            this.currentStepConfig?.expectedWeight == null ||
            this.currentStepConfig?.weightTolerance == null) {
            this.currentStep!.withinTolerance = null;
            return;
        }

        const actual = this.currentStep.actualWeight;
        const { min, max } = this.weightRange;
        this.currentStep.withinTolerance = actual >= min && actual <= max;
    }

    onPhotoSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files[0]) {
            this.currentStepPhotoFile = input.files[0];

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                this.currentStepPhotoPreview = e.target?.result as string;
            };
            reader.readAsDataURL(this.currentStepPhotoFile);
        }
    }

    clearPhoto() {
        this.currentStepPhotoFile = null;
        this.currentStepPhotoPreview = null;
        if (this.$refs.photoInput) {
            (this.$refs.photoInput as HTMLInputElement).value = '';
        }
    }

    async saveCurrentStep() {
        if (!this.batch || !this.currentStep) return;

        try {
            // Upload photo if present
            let photoId = this.currentStep.photoId;
            if (this.currentStepPhotoFile) {
                photoId = await this.uploadPhoto(this.currentStepPhotoFile);
            }

            // Save step data
            const stepData = {
                stepNumber: this.currentStep.stepNumber,
                actualWeight: this.currentStep.actualWeight,
                withinTolerance: this.currentStep.withinTolerance,
                photoId: photoId
            };

            await Axios.post(`/CompoundBatch/${this.batch.id}/step`, stepData);
        } catch (error) {
            console.error('Error saving step', error);
            this.$notification(NotificationOptions.error(error));
            throw error;
        }
    }

    async uploadPhoto(file: File): Promise<number> {
        const formData = new FormData();
        formData.append('files', file);

        try {
            // Create a new axios instance with baseURL but without the global application/json header
            // This allows multipart/form-data to be set correctly with boundary
            const axiosInstance = Axios.create({
                baseURL: Axios.defaults.baseURL
            });
            delete axiosInstance.defaults.headers.post['Content-Type'];

            const response = await axiosInstance.post<number[]>('/Image/SaveDocument?bypassIngestion=true', formData);

            if (!response.data || response.data.length === 0) {
                throw new Error('No image ID returned from upload');
            }

            return response.data[0];
        } catch (error) {
            console.error('Error uploading photo:', error);
            throw error;
        }
    }

    async saveAllIngredients() {
        if (!this.batch) return;

        try {
            const savePromises = this.scaledIngredients
                .map(ingredient => {
                    const ingredientData = {
                        drugID: ingredient.drugID,
                        drugSource: ingredient.drugSource,
                        quantity: ingredient.scaledQuantity,
                        unitOfMeasure: ingredient.unitOfMeasure,
                        lotNumber: ingredient.lotNumber || null,
                        expirationDate: ingredient.expirationDate || null
                    };

                    return Axios.post(`/CompoundBatch/${this.batch!.id}/ingredient`, ingredientData);
                });

            await Promise.all(savePromises);
        } catch (error) {
            console.error('Error saving ingredients:', error);
            this.$notification(NotificationOptions.error(error));
            throw error;
        }
    }

    async nextStep() {
        await this.saveCurrentStep();
        this.currentStepIndex++;
        this.currentStepPhotoFile = null;
        this.currentStepPhotoPreview = null;
    }

    async previousStep() {
        if (this.currentStepIndex > 0) {
            this.currentStepIndex--;
            this.currentStepPhotoFile = null;
            this.currentStepPhotoPreview = null;
        }
    }

    async completeBatch() {
        await this.saveCurrentStep();

        // Prepopulate store if in batch mode
        if (this.modeInternal === 'batch' && this.batch?.storeId) {
            try {
                const storeResponse = await Axios.get(`/Store/${this.batch.storeId}`);
                this.selectedStore = storeResponse.data;
            } catch (error) {
                console.warn('Error loading batch store:', error);
            }
        }

        // Prepopulate package size from the compound definition (for both batch and prescription modes)
        if (!this.packageSize && this.selectedCompound?.packageSize) {
            this.packageSize = this.selectedCompound.packageSize;
        }

        this.showCompletionModal = true;
    }

    async finalizeBatch() {
        if (!this.batch) return;

        try {
            // Save all ingredients first
            await this.saveAllIngredients();

            const completionData = {
                lotNumber: this.lotNumber,
                beyondUseDate: this.beyondUseDate ? new Date(this.beyondUseDate) : null,
                packageSize: this.packageSize,
                storeId: this.selectedStore?.id || this.batch.storeId,
                programId: this.selectedProgram?.id || null,
                locationId: this.selectedLocation?.id || null
            };

            const completeResponse = await Axios.post(`/CompoundBatch/${this.batch.id}/complete`, completionData);
            const completedBatch = completeResponse.data;

            // Check if batch went to AwaitingVerification
            if (completedBatch && completedBatch.status === 3) {
                this.$notification(NotificationOptions.success('Batch submitted for pharmacist verification. Inventory will be posted after verification is complete.'));
                this.$router.push({ name: 'Compound-Drug', params: { id: this.selectedCompound!.compoundID!.toString() } });
                return;
            }

            // Print labels for batch using client service
            if (this.modeInternal === 'batch') {
                try {
                    // Get ZPL data from the API
                    const zplRequest = {
                        storeId: this.selectedStore?.id || this.batch.storeId,
                        labelCount: this.labelCount
                    };
                    const zplResponse = await Axios.post(`/CompoundBatch/${this.batch.id}/label-zpl`, zplRequest);
                    this.labelZpl = zplResponse.data;

                    // Print labels using client service (no numbering for inventory batches)
                    await this.sendLabelZplToClientService(false);
                    this.$notification(NotificationOptions.success(`Batch completed successfully! ${this.labelCount} label(s) sent to printer.`));
                } catch (labelError) {
                    console.error('Error printing batch labels', labelError);
                    this.$notification(NotificationOptions.warning('Batch completed but label printing failed. Please print labels manually.'));
                }
            } else if (this.modeInternal === 'prescription') {
                try {
                    // Get ZPL data from the API for prescription
                    const zplRequest = {
                        storeId: this.batch.storeId,
                        labelCount: this.labelCount
                    };
                    const zplResponse = await Axios.post(`/CompoundBatch/${this.batch.id}/label-zpl`, zplRequest);
                    this.labelZpl = zplResponse.data;

                    // Print labels using client service (with numbering only if multiple labels)
                    const applyNumbering = this.labelCount > 1;
                    await this.sendLabelZplToClientService(applyNumbering);
                    this.$notification(NotificationOptions.success(`Batch completed successfully! ${this.labelCount} label(s) sent to printer.`));
                } catch (labelError) {
                    console.error('Error printing batch labels', labelError);
                    this.$notification(NotificationOptions.warning('Batch completed but label printing failed. Please print labels manually.'));
                }
            } else {
                this.$notification(NotificationOptions.success('Batch completed successfully!'));
            }

            // Redirect based on mode
            if (this.modeInternal === 'prescription' && this.selectedPrescription.rxNumber) {
                // For prescription mode, redirect to prescription details
                this.$router.push({
                    name: 'Prescription',
                    params: {
                        storeID: this.selectedPrescription.storeID.toString(),
                        rxNumber: this.selectedPrescription.rxNumber.toString(),
                        rfNumber: (this.selectedPrescription.rfNumber || 0).toString()
                    }
                });
            } else {
                // For batch mode, redirect to compound details
                this.$router.push({ name: 'Compound-Drug', params: { id: this.selectedCompound!.compoundID!.toString() } });
            }
        } catch (error) {
            console.error('Error completing batch', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async sendLabelZplToClientService(applyNumbering: boolean = false): Promise<boolean> {
        if (!this.labelZpl) {
            this.$notification(NotificationOptions.error("No label data to print"));
            return false;
        }

        try {
            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Print warnings via WebSocket
                if (this.labelZpl.warnings?.length) {
                    const warningsRequest = this.buildPrinterRequest(
                        this.labelZpl.warnings.join(""),
                        `${this.batch?.batchNumber}-warnings`
                    );
                    workerPort.postMessage(["clientServicePrintRaw", warningsRequest]);
                    await new Promise(resolve => setTimeout(resolve, 100));
                }

                // Print labels via WebSocket
                for (let i = 1; i <= this.labelCount; i++) {
                    // Only apply numbering if requested (for prescriptions, not inventory batches)
                    const modifiedZpl = applyNumbering
                        ? this.processLabelNumbering(this.labelZpl.primary, i, this.labelCount)
                        : this.labelZpl.primary;
                    const labelRequest = this.buildPrinterRequest(
                        modifiedZpl,
                        `${this.batch?.batchNumber}-label-${i}`
                    );
                    workerPort.postMessage(["clientServicePrintRaw", labelRequest]);
                    await new Promise(resolve => setTimeout(resolve, 100));
                }

                return true;
            }

            // Fallback to HTTP if WebSocket not available
            const printerEndpoint = "http://localhost:7274/print/raw";

            if (this.labelZpl.warnings?.length) {
                const warningsRequest = this.buildPrinterRequest(
                    this.labelZpl.warnings.join(""),
                    `${this.batch?.batchNumber}-warnings`
                );
                await Axios.post(printerEndpoint, warningsRequest);
            }

            for (let i = 1; i <= this.labelCount; i++) {
                // Only apply numbering if requested (for prescriptions, not inventory batches)
                const modifiedZpl = applyNumbering
                    ? this.processLabelNumbering(this.labelZpl.primary, i, this.labelCount)
                    : this.labelZpl.primary;
                const labelRequest = this.buildPrinterRequest(
                    modifiedZpl,
                    `${this.batch?.batchNumber}-label-${i}`
                );
                await Axios.post(printerEndpoint, labelRequest);
            }

            return true;
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
            return false;
        }
    }

    buildPrinterRequest(rawContent: string, documentName: string) {
        return {
            printerName: this.activeLocalPrinter?.name,
            rawContent: rawContent,
            documentName: documentName,
            copies: 1
        };
    }

    processLabelNumbering(zplContent: string, currentLabel: number, totalLabels: number): string {
        if (totalLabels > 1) {
            return zplContent.replace('{{LabelCount}}', `${currentLabel} of ${totalLabels}`);
        } else {
            return zplContent.replace('{{LabelCount}}', '');
        }
    }

    async getSupportedClientService() {
        try {
            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Request version through the shared worker
                const version = await new Promise<string | null>((resolve) => {
                    const timeout = setTimeout(() => {
                        console.log("WebSocket version request timed out, falling back to HTTP");
                        resolve(null);
                    }, 3000);

                    const handleMessage = (e: MessageEvent) => {
                        if (e.data[0] === "clientServiceVersion") {
                            clearTimeout(timeout);
                            workerPort.removeEventListener("message", handleMessage);
                            resolve(e.data[1] || null);
                        }
                    };

                    workerPort.addEventListener("message", handleMessage);
                    // The version should already be cached, but we can request a refresh
                    workerPort.postMessage(["clientServiceRefreshVersion", null]);
                });

                if (version) {
                    this.hasSupportedClientService = compareVersions(version, minClientVersion) >= 0;
                    return;
                }
            }

            // Fallback to HTTP if WebSocket not available or failed
            const response = await this.$http.get("http://localhost:7274/version");
            const version = response.data;

            this.hasSupportedClientService = compareVersions(version, minClientVersion) >= 0;
        } catch {
            this.hasSupportedClientService = false;
        }
    }

    async getLocalPrinters() {
        try {
            this.isLoadingPrinters = true;

            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Request printers through the shared worker
                const printers = await new Promise<any[]>((resolve) => {
                    const timeout = setTimeout(() => {
                        console.log("WebSocket printer request timed out, falling back to HTTP");
                        resolve([]);
                    }, 3000);

                    const handleMessage = (e: MessageEvent) => {
                        if (e.data[0] === "clientServicePrinters") {
                            clearTimeout(timeout);
                            workerPort.removeEventListener("message", handleMessage);
                            resolve(e.data[1] || []);
                        }
                    };

                    workerPort.addEventListener("message", handleMessage);
                    workerPort.postMessage(["clientServiceRefreshPrinters", null]);
                });

                if (printers.length > 0) {
                    this.localPrinters = printers;

                    this.localPrinters.sort((a, b) => {
                        const aHasZPL = a.name.toLowerCase().includes('zpl') || a.name.toLowerCase().includes('zebra');
                        const bHasZPL = b.name.toLowerCase().includes('zpl') || b.name.toLowerCase().includes('zebra');

                        if (aHasZPL && !bHasZPL) return -1;
                        if (!aHasZPL && bHasZPL) return 1;

                        return a.name.localeCompare(b.name);
                    });

                    this.handleDefaultPrinter();
                    return;
                }
            }

            // Fallback to HTTP if WebSocket not available or no printers received
            const response = await this.$http.get("http://localhost:7274/print/devices");
            this.localPrinters = response.data;

            this.localPrinters.sort((a, b) => {
                const aHasZPL = a.name.toLowerCase().includes('zpl') || a.name.toLowerCase().includes('zebra');
                const bHasZPL = b.name.toLowerCase().includes('zpl') || b.name.toLowerCase().includes('zebra');

                if (aHasZPL && !bHasZPL) return -1;
                if (!aHasZPL && bHasZPL) return 1;

                return a.name.localeCompare(b.name);
            });

            this.handleDefaultPrinter();
        } catch {
            //ignore
        } finally {
            this.isLoadingPrinters = false;
        }
    }

    handleDefaultPrinter() {
        const lastPrinter = localStorage.getItem("active-compound-batch-printer");
        if (lastPrinter) {
            this.activeLocalPrinter = this.localPrinters?.find(p => p.name === lastPrinter);
            return;
        }

        if (this.localPrinters.length) {
            this.activeLocalPrinter = this.localPrinters[0];
        }
    }

    @Watch('activeLocalPrinter')
    activeLocalPrinterChanged() {
        localStorage.setItem("active-compound-batch-printer", this.activeLocalPrinter?.name);
    }

    get hasLocalPrinters() {
        return !!this.localPrinters.length;
    }

    prettyPrinterName(printer: any) {
        if (!printer) return "No Printer Defined";
        if (!printer.serverName) return printer.name;
        return printer.name.replace(printer.serverName, "").replace(/\\/g, "");
    }
}
</script>

<style scoped>
.img-thumbnail {
    max-height: 300px;
    object-fit: contain;
}
</style>
